import { observer } from 'mobx-react';
import { useState } from 'react';
import {
  Box,
  Button,
  Collapse,
  Divider,
  Flex,
  Text,
  UnstyledButton,
} from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { AppointmentType } from '@prisma/client';
import { MyIcon } from '@web/common-ui';
import { useWaitingStateStore } from '../../../../store';
import { reschedule } from '../book-in-person/cal-dot-com.links';
import { NachversandAppointmentDetails } from './NachversandAppointmentDetails';
import {
  isAppointmentAfterDeadline,
  isAppointmentStillCurrent,
  isNachversandBannerVisible,
  isPastAppointmentHintVisible,
} from './nachversandBannerState';

const COLLAPSE_ID = 'nachversand-collapse';
// readable amber on the near-white banner background; the mockup's #E8A317 is a
// border colour and fails contrast as text
export const WARNING_COLOR = '#C2700A';

/**
 * Everything the participant needs for the Nachversand video call, in one collapsible row
 * of the waiting-screen banner stack: the booking CTA before, the appointment after. Which
 * Nachversand that is — BDM1 or BDM2 — comes from the store as activeNachversand; the BDM
 * steps deliberately carry no booking copy or button of their own (stepsData).
 *
 * Not rendered when there is nothing to do: the booking window is only open while the
 * participant may actually book and only until the booking deadline, so "survey still open"
 * and "deadline passed" are covered by it. A current appointment always shows, unless the
 * shop already dispatched the sample or staff revoked the eligibility afterwards
 * (isNachversandBannerVisible).
 */
export const NachversandBanner = observer(() => {
  const { t } = useTranslation('waiting-screen');
  const [expanded, setExpanded] = useState(false);
  const { activeNachversand, nachversandEligible } = useWaitingStateStore();

  if (activeNachversand === null) {
    return null;
  }
  const {
    kind,
    bookingLink,
    appointment,
    settled,
    sampleDeadline,
    bookingWindowOpen,
  } = activeNachversand;

  const isBooked = isAppointmentStillCurrent(appointment);
  const isTooLate = isAppointmentAfterDeadline(appointment, sampleDeadline);
  // only a booked appointment can be rescheduled — a past one is bookable again instead
  const showWarning = isBooked && isTooLate;

  const showPastAppointmentHint = isPastAppointmentHintVisible(
    settled,
    appointment,
    isBooked,
  );
  // a past appointment is presented like a current one, so its reschedule link stays reachable
  const showAppointment = isBooked || showPastAppointmentHint;

  const isVisible = isNachversandBannerVisible(
    settled,
    nachversandEligible,
    isBooked,
    bookingWindowOpen,
  );
  if (!isVisible) {
    return null;
  }

  const deadline = sampleDeadline?.toFormat('dd.MM.yyyy') ?? '';
  // BDM1 ships the 12 % flowers, BDM2 4 g of whatever product was drawn — the paragraphs that
  // name the product therefore exist per kind
  const variant = kind === AppointmentType.NACHVERSAND_BDM2 ? 'Bdm2' : '';
  const productText = (key: string, options?: Record<string, string>) =>
    t(`Nachversand.${key}${variant}`, options);

  return (
    <Box
      p="12px 20px 12px 16px"
      w="100%"
      bg="#FBFBF8"
      style={{
        borderLeft: '4px solid #3D7F53',
        borderBottom: '1px solid #edece8',
      }}
    >
      <UnstyledButton
        onClick={() => setExpanded((prev) => !prev)}
        w="100%"
        display="flex"
        style={{ alignItems: 'center' }}
        type="button"
        aria-expanded={expanded}
        aria-controls={COLLAPSE_ID}
      >
        <Flex justify="space-between" align="center" w="100%">
          <Flex align="center" gap="12px">
            <Flex
              w={42}
              align="center"
              justify="center"
              style={{ flexShrink: 0 }}
            >
              <MyIcon name="videocam" style={{ width: 28, height: 28 }} />
            </Flex>
            {/* the warning lives in the TITLE, not in the short description: the latter is
                hidden below sm, so on mobile the warning would never be seen */}
            <Text
              fw={700}
              size="16px"
              c={showWarning ? WARNING_COLOR : '#202020'}
              lh="1"
            >
              {showWarning
                ? t('Nachversand.Booked.AfterDeadlineTitle')
                : showAppointment
                  ? t('Nachversand.Booked.Title')
                  : t('Nachversand.Bookable.Title')}
            </Text>
            {/* a booked appointment needs no summary line — the title says it, and the
                details are one click away. Only the open call to action gets one. */}
            {!showAppointment && (
              <Text size="16px" c="#666" visibleFrom="sm" lh="1">
                {t('Nachversand.Bookable.ShortDescription', { deadline })}
              </Text>
            )}
          </Flex>
          <MyIcon
            name={expanded ? 'arrow_up' : 'arrow_down'}
            style={{ flexShrink: 0, display: 'block' }}
          />
        </Flex>
      </UnstyledButton>

      <Collapse in={expanded} id={COLLAPSE_ID}>
        {expanded && (
          <Box pt="16px">
            {/* the warning brackets the appointment details with a divider of its own —
                without it the details already carry only the closing one */}
            {showWarning && (
              <>
                <Text size="16px" c={WARNING_COLOR} lh="1.6" mb="14px">
                  {productText('Booked.AfterDeadline', { deadline })}
                </Text>
                <Divider mb="16px" />
              </>
            )}

            {/* The call most likely took place and only the dispatch click is missing: nothing
                left to prepare and nothing to book, so the hint and the appointment stand alone.
                Re-booking a real no-show goes through the appointment's own reschedule link. */}
            {showPastAppointmentHint && (
              <>
                <Text size="16px" c={WARNING_COLOR} lh="1.6" mb="16px">
                  {productText('Bookable.PastAppointment')}
                </Text>
                <Divider mb="16px" />
              </>
            )}

            {showAppointment && appointment !== null ? (
              <>
                <NachversandAppointmentDetails
                  appointment={appointment}
                  withTrailingDivider={!showPastAppointmentHint}
                />
                {/* the pencil next to the date is too quiet when moving the call is the only
                    thing left to do */}
                {showPastAppointmentHint && (
                  <Button
                    component="a"
                    href={reschedule(appointment.id)}
                    target="_self"
                    rel="noreferrer"
                    size="sm"
                    mt="16px"
                  >
                    {t('Nachversand.Booked.Reschedule')}
                  </Button>
                )}
              </>
            ) : (
              <>
                <Text size="16px" c="#202020" lh="1.6" mb="16px">
                  {productText('Bookable.Description')}
                </Text>
                <Button
                  component="a"
                  href={bookingLink ?? undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                  size="sm"
                  mb="20px"
                  disabled={!bookingLink}
                >
                  {t('Nachversand.Bookable.Button')}
                </Button>
              </>
            )}

            {!showPastAppointmentHint && (
              <>
                <Text size="16px" c="#202020" lh="1.6" mb="12px">
                  {t('Nachversand.Prepare.BringAlong')}
                  {/* the warning already states the deadline — no need to say it twice */}
                  {!showWarning && (
                    <>
                      {' '}
                      <Text span inherit fw={700} c="#4A4A48">
                        {productText('Prepare.Deadline', { deadline })}
                      </Text>
                    </>
                  )}
                </Text>
                <Text size="16px" c="#202020" lh="1.6">
                  {productText('Prepare.AfterCall')}
                </Text>
              </>
            )}
          </Box>
        )}
      </Collapse>
    </Box>
  );
});
